import React from "react";
import { useDispatch, useSelector } from "react-redux";
import "./SignUp.css";
import signupImage from "../../../assests/auth pages.png";
import googleLogo from "../../../assests/google-logo.png";
import { Link, useNavigate } from "react-router-dom";
import { validateName, validateEmail, validatePassword } from "../../common/Validation";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { SIGNUP_URL } from "../../../config/config";
import {
  setFirstName,
  setLastName,
  setEmail,
  setPassword,
  setFirstNameError,
  setLastNameError,
  setEmailError,
  setPasswordError,
  setSignupError,
  setSignupSuccess,
  setTokens,
  setUser
} from "../../../slices/authSlice";
import { useGoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    firstName,
    lastName,
    email,
    password,
    firstNameError,
    lastNameError,
    emailError,
    passwordError,
  } = useSelector((state) => state.auth);

  const handleSubmit = async (event) => {
    event.preventDefault();

    let isValid = true;

    const firstNameValidation = validateName(firstName);
    if (firstNameValidation) {
      dispatch(setFirstNameError(firstNameValidation));
      isValid = false;
    } else {
      dispatch(setFirstNameError(""));
    }

    const lastNameValidation = validateName(lastName);
    if (lastNameValidation) {
      dispatch(setLastNameError(lastNameValidation));
      isValid = false;
    } else {
      dispatch(setLastNameError(""));
    }

    const emailValidation = validateEmail(email);
    if (emailValidation) {
      dispatch(setEmailError(emailValidation));
      isValid = false;
    } else {
      dispatch(setEmailError(""));
    }

    const passwordValidation = validatePassword(password);
    if (passwordValidation) {
      dispatch(setPasswordError(passwordValidation));
      isValid = false;
    } else {
      dispatch(setPasswordError(""));
    }

    if (isValid) {
      try {
        const response = await fetch(SIGNUP_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ firstName, lastName, email, password }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (data.message && data.message.includes("Email already exists")) {
            dispatch(setSignupError("Email already exists. Please use a different email."));
            toast.error("Email already exists. Please use a different email.");
          } else {
            dispatch(setSignupError("Sign-up failed. Please try again."));
            toast.error("Sign-up failed. Please try again.");
          }
          return;
        }

        console.log("Sign-up successful!");
        handleSignUpSuccess(data);
      } catch (error) {
        console.error("Error during sign-up:", error);
        dispatch(setSignupError("Something went wrong. Please try again."));
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  const googleSignUp = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      console.log("Google sign-up successful. Code Response:", codeResponse);
      try {
        const tokenResponse = await exchangeCodeForTokens(codeResponse.code);
        console.log("Token Response:", tokenResponse);

        if (!tokenResponse.id_token) {
          console.error("ID token is missing from the token response");
          toast.error("Google sign-up failed. Please try again.");
          return;
        }

        const backendResponse = await fetch(SIGNUP_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ google_token: tokenResponse.id_token }),
        });

        console.log("Backend response status:", backendResponse.status);
        const responseText = await backendResponse.text();
        console.log("Backend response text:", responseText);

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (error) {
          console.error("Error parsing JSON:", error);
          toast.error("Server response is not valid JSON. Please try again later.");
          return;
        }

        if (data.status === 1) {
          handleSignUpSuccess(data);
        } else {
          toast.error(data.message || "Google sign-up failed. Please try again.");
        }
      } catch (error) {
        console.error("Error during Google sign-up:", error);
        toast.error("Something went wrong. Please try again.");
      }
    },
    onError: (error) => {
      console.error('Google Sign-Up Failed:', error);
      toast.error("Google sign-up failed. Please try again.");
    },
  });

  const exchangeCodeForTokens = async (code) => {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code: code,
        client_id: '387965829154-gnmj49v7bood1ibtg8t2901f5kpl7dvk.apps.googleusercontent.com',
        client_secret: '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',
        redirect_uri: 'http://localhost:3000',
        grant_type: 'authorization_code',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for tokens');
    }

    return response.json();
  };

  const handleSignUpSuccess = (data) => {
    const { accessToken, refreshToken } = data;
    const decodedToken = jwtDecode(accessToken);
    const userId = decodedToken.data.userId;

    dispatch(setTokens({ accessToken, refreshToken, userId }));
    dispatch(setUser({
      userId: decodedToken.data.userId,
      firstName: decodedToken.data.fName,
      lastName: decodedToken.data.lName,
      email: decodedToken.data.email
    }));
    dispatch(setSignupSuccess("User has been registered successfully."));
    toast.success("User has been registered successfully.");
    navigate("/home");
  };

  return (
    <div className="signup-page">
      <div className="signup-grid">
        <div className="signup-image">
          <img src={signupImage} alt="SignUp Visual" />
        </div>
        <div className="signup-form-container">
          <div className="signup-form">
            <form onSubmit={handleSubmit}>
              <div className="styleName">Create an Account</div>
              <p>Enter your details to create an account</p>
              <label htmlFor="first-name">First Name</label>
              <input
                type="text"
                id="first-name"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => dispatch(setFirstName(e.target.value))}
                maxLength="100"
                required
              />
              {firstNameError && (
                <div className="error-message">{firstNameError}</div>
              )}
              <label htmlFor="last-name">Last Name</label>
              <input
                type="text"
                id="last-name"
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => dispatch(setLastName(e.target.value))}
                maxLength="100"
                required
              />
              {lastNameError && (
                <div className="error-message">{lastNameError}</div>
              )}
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => dispatch(setEmail(e.target.value))}
                maxLength="100"
                required
              />
              {emailError && <div className="error-message">{emailError}</div>}
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => dispatch(setPassword(e.target.value))}
                maxLength="20"
                required
              />
              {passwordError && (
                <div className="error-message">{passwordError}</div>
              )}
              <div className="forgot-password">
                <a href="/forgot-password">Forgot password</a>
              </div>
              <button type="submit" style={{ backgroundColor: "#EDA107" }}>
                Sign Up
              </button>
              <button type="button" className="google-signin" onClick={() => googleSignUp()}>
                <img src={googleLogo} alt="Google Logo" /> Sign Up with Google
              </button>
              <div className="signin-link">
                Already have an account? <Link to="/login">Sign in</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SignUp;